import React, { useEffect, useState } from "react";
import { Star, Quote, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function Stars({ value, onChange, size = 4 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          data-testid={onChange ? `review-rating-${n}` : undefined}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}
        >
          <Star
            className={`h-${size} w-${size} ${
              n <= value ? "fill-[#E8611A] text-[#E8611A]" : "fill-transparent text-slate-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", comment: "" });
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    api.get("/reviews").then((res) => setReviews(res.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Informe seu nome.");
    if (!form.comment.trim()) return toast.error("Escreva um comentário.");
    setSubmitting(true);
    try {
      const res = await api.post("/reviews", {
        name: form.name.trim(),
        rating,
        comment: form.comment.trim(),
      });
      setReviews((prev) => [res.data, ...prev]);
      setForm({ name: "", comment: "" });
      setRating(5);
      toast.success("Obrigado pela sua avaliação!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="reviews-section" className="mx-auto mt-16 max-w-6xl px-5">
      <div className="flex items-center gap-2">
        <Quote className="h-6 w-6 text-[#1B7A45]" />
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
          O que dizem por aí
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Avaliações de quem já jogou na Dubal Esportes.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Form */}
        <div
          data-testid="review-form"
          className="animate-fade-up rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-1"
        >
          <h3 className="font-display text-lg font-bold text-slate-900">Deixe sua avaliação</h3>
          <div className="mt-3 space-y-3">
            <Input
              data-testid="review-form-name"
              placeholder="Seu nome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl border-slate-200"
            />
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Sua nota</span>
              <Stars value={rating} onChange={setRating} size={5} />
            </div>
            <Textarea
              data-testid="review-form-comment"
              placeholder="Como foi sua experiência?"
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              rows={3}
              className="rounded-xl border-slate-200"
            />
            <Button
              data-testid="review-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="w-full rounded-full bg-[#1B7A45] py-5 font-semibold text-white hover:bg-[#156337] disabled:opacity-50"
            >
              <Send className="mr-2 h-4 w-4" />
              {submitting ? "Enviando..." : "Enviar avaliação"}
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2">
          {reviews.length === 0 ? (
            <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white py-12 text-center">
              <p className="font-semibold text-slate-500">Seja o primeiro a avaliar!</p>
            </div>
          ) : (
            reviews.slice(0, 8).map((r, i) => (
              <div
                key={r.id}
                data-testid={`review-card-${r.id}`}
                className="animate-fade-up flex flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <Stars value={r.rating} />
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">
                  "{r.comment}"
                </p>
                <p className="mt-4 font-display text-base font-bold text-slate-900">{r.name}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
